import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Icon,
  IconButton,
  Checkbox,
} from "@mui/material";
import {
  Permission,
  UserPermission,
  UserRole,
  Role,
} from "./UserManagement.interface";
import React, { useState } from "react";
import moment from "moment";

import {
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { UserPermissions } from "./permissionsTabs/userPermissions";
import { RolesMatrix } from "./permissionsTabs/rolesMatrix";
import { RolesDefinition } from "./permissionsTabs/rolesDefinition";

interface PermissionsMatrixProps {
  moduleKey: string;
  moduleColor: string;
  selectedLocationId: string;
  userPermissions: UserPermission[];
  userRoles: UserRole[];
  onLocationChange: (locationId: string) => void;
  onPermissionToggle: (userId: string, permissionId: string) => void;
  onRoleToggle: (userId: string, roleId: string) => void;
  activeTab: number;
  onTabChange: (tab: number) => void;
  // Real data props
  users: Array<{
    _id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    newPermissions?: any;
    newRoles?: any[];
  }>;
  locations: Array<{
    [key: string]: any;
    id: string;
    name: string;
    type: "branch" | "warehouse" | "department";
  }>;
  systemPermissions: Array<{
    permission_code: string;
    module: string;
    group: string;
    action: string;
    description?: string;
    is_active: boolean;
  }>;
  roles: Array<{
    _id?: string;
    role_name: string;
    description?: string;
    module: string;
    permissions: string[];
  }>;
  // Add new props for saving
  saving?: boolean;
  pendingChangesCount?: number;
  onSavePermissionChanges?: () => void;
}

// Accounting module color scheme
const accountingColors = {
  primary: "#5C5674",
  secondary: "#7F79A2",
  accent: "#A9A3C8",
  light: "#F5F4F8",
  error: "#c0392b",
  success: "#27ae60",
  warning: "#f39c12",
  info: "#3498db",
};

const mockRoles = [
  {
    id: "admin",
    name: "Administrator",
    description: "Full access to all features",
    permissions: [
      "hr.dashboard.view",
      "hr.employees.view",
      "hr.employees.create",
      "hr.employees.edit",
      "hr.employees.delete",
      "hr.payroll.view",
      "hr.payroll.process",
      "hr.attendance.view",
    ],
    created_at: moment().subtract(30, "days").valueOf(),
    updated_at: moment().subtract(1, "days").valueOf(),
  },
  {
    id: "manager",
    name: "Manager",
    description: "Management level access",
    permissions: [
      "hr.dashboard.view",
      "hr.employees.view",
      "hr.employees.create",
      "hr.employees.edit",
      "hr.payroll.view",
      "hr.attendance.view",
    ],
    created_at: moment().subtract(20, "days").valueOf(),
    updated_at: moment().subtract(5, "days").valueOf(),
  },
  {
    id: "supervisor",
    name: "Supervisor",
    description: "Supervisory access",
    permissions: [
      "hr.dashboard.view",
      "hr.employees.view",
      "hr.attendance.view",
    ],
    created_at: moment().subtract(15, "days").valueOf(),
    updated_at: moment().subtract(3, "days").valueOf(),
  },
  {
    id: "staff",
    name: "Staff",
    description: "Basic staff access",
    permissions: ["hr.dashboard.view", "hr.attendance.view"],
    created_at: moment().subtract(10, "days").valueOf(),
    updated_at: moment().subtract(2, "days").valueOf(),
  },
];

const getModulePermissions = (moduleKey: string): Permission[] => {
  const basePermissions = {
    hr: [
      {
        id: "hr.dashboard.view",
        name: "View Dashboard",
        description: "Access HR dashboard",
        category: "Dashboard",
      },
      {
        id: "hr.employees.view",
        name: "View Employees",
        description: "View employee list",
        category: "Employees",
      },
      {
        id: "hr.employees.create",
        name: "Create Employees",
        description: "Add new employees",
        category: "Employees",
      },
      {
        id: "hr.employees.edit",
        name: "Edit Employees",
        description: "Modify employee data",
        category: "Employees",
      },
      {
        id: "hr.employees.delete",
        name: "Delete Employees",
        description: "Remove employees",
        category: "Employees",
      },
      {
        id: "hr.payroll.view",
        name: "View Payroll",
        description: "Access payroll data",
        category: "Payroll",
      },
      {
        id: "hr.payroll.process",
        name: "Process Payroll",
        description: "Run payroll processing",
        category: "Payroll",
      },
      {
        id: "hr.attendance.view",
        name: "View Attendance",
        description: "Track attendance",
        category: "Attendance",
      },
    ],
    accounting: [
      {
        id: "accounting.dashboard.view",
        name: "View Dashboard",
        description: "Access accounting dashboard",
        category: "Dashboard",
      },
      {
        id: "accounting.coa.view",
        name: "View Chart of Accounts",
        description: "Access chart of accounts",
        category: "Accounts",
      },
      {
        id: "accounting.coa.edit",
        name: "Edit Chart of Accounts",
        description: "Modify chart of accounts",
        category: "Accounts",
      },
      {
        id: "accounting.journals.view",
        name: "View Journals",
        description: "Access journal entries",
        category: "Journals",
      },
      {
        id: "accounting.journals.create",
        name: "Create Journals",
        description: "Add journal entries",
        category: "Journals",
      },
      {
        id: "accounting.vouchers.view",
        name: "View Vouchers",
        description: "Access vouchers",
        category: "Vouchers",
      },
      {
        id: "accounting.vouchers.create",
        name: "Create Vouchers",
        description: "Create new vouchers",
        category: "Vouchers",
      },
      {
        id: "accounting.ledger.view",
        name: "View Ledger",
        description: "Access ledger reports",
        category: "Reports",
      },
    ],
    inventory: [
      {
        id: "inventory.dashboard.view",
        name: "View Dashboard",
        description: "Access inventory dashboard",
        category: "Dashboard",
      },
      {
        id: "inventory.products.view",
        name: "View Products",
        description: "View product catalog",
        category: "Products",
      },
      {
        id: "inventory.products.create",
        name: "Create Products",
        description: "Add new products",
        category: "Products",
      },
      {
        id: "inventory.products.edit",
        name: "Edit Products",
        description: "Modify product data",
        category: "Products",
      },
      {
        id: "inventory.stock.view",
        name: "View Stock",
        description: "Check stock levels",
        category: "Stock",
      },
      {
        id: "inventory.stock.adjust",
        name: "Adjust Stock",
        description: "Modify stock levels",
        category: "Stock",
      },
      {
        id: "inventory.orders.view",
        name: "View Orders",
        description: "Access purchase orders",
        category: "Orders",
      },
      {
        id: "inventory.orders.create",
        name: "Create Orders",
        description: "Create purchase orders",
        category: "Orders",
      },
    ],
    pos: [
      {
        id: "pos.dashboard.view",
        name: "View Dashboard",
        description: "Access POS dashboard",
        category: "Dashboard",
      },
      {
        id: "pos.sales.view",
        name: "View Sales",
        description: "Access sales data",
        category: "Sales",
      },
      {
        id: "pos.sales.create",
        name: "Process Sales",
        description: "Handle sales transactions",
        category: "Sales",
      },
      {
        id: "pos.returns.view",
        name: "View Returns",
        description: "Access return data",
        category: "Returns",
      },
      {
        id: "pos.returns.process",
        name: "Process Returns",
        description: "Handle return transactions",
        category: "Returns",
      },
      {
        id: "pos.cashier.manage",
        name: "Manage Cashiers",
        description: "Manage cashier accounts",
        category: "Management",
      },
      {
        id: "pos.reports.view",
        name: "View Reports",
        description: "Access POS reports",
        category: "Reports",
      },
      {
        id: "pos.discounts.apply",
        name: "Apply Discounts",
        description: "Apply discounts to sales",
        category: "Sales",
      },
    ],
  };

  return basePermissions[moduleKey as keyof typeof basePermissions] || [];
};

export const PermissionsMatrix: React.FC<PermissionsMatrixProps> = ({
  moduleKey,
  moduleColor,
  selectedLocationId,
  userPermissions,
  userRoles,
  onLocationChange,
  onPermissionToggle,
  onRoleToggle,
  activeTab,
  onTabChange,
  users,
  locations,
  systemPermissions,
  roles: availableRoles,
  saving,
  pendingChangesCount,
  onSavePermissionChanges,
}) => {
  const theme = useTheme();
  // Note: Mock roles and related functions removed since RolesDefinition component
  // now handles all role management internally with real API data

  // Use accounting colors instead of the passed moduleColor
  const themeColor = accountingColors.primary;

  // Get permissions for current module and organize by category
  const modulePermissions = getModulePermissions(moduleKey);
  const groupedPermissions = modulePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Create permission modules structure similar to PermissionsOverview
  const permissionModules = Object.entries(groupedPermissions).map(
    ([category, permissions]) => ({
      key: category.toLowerCase(),
      label: category,
      icon: getIconForCategory(category),
      color: getCategoryColor(category),
      permissions: permissions.map((p) => p.id),
    })
  );

  function getIconForCategory(category: string): string {
    const iconMap: Record<string, string> = {
      Dashboard: "dashboard",
      Employees: "people",
      Payroll: "payments",
      Attendance: "schedule",
      Accounts: "account_tree",
      Journals: "book",
      Vouchers: "receipt",
      Reports: "table_rows",
      Products: "inventory",
      Stock: "warehouse",
      Orders: "shopping_cart",
      Sales: "point_of_sale",
      Returns: "keyboard_return",
      Management: "admin_panel_settings",
    };
    return iconMap[category] || "settings";
  }

  function getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      Dashboard: "#8B5CF6",
      Employees: "#F59E0B",
      Payroll: "#10B981",
      Attendance: "#06B6D4",
      Accounts: "#EF4444",
      Journals: "#8B5CF6",
      Vouchers: "#F59E0B",
      Reports: "#10B981",
      Products: "#06B6D4",
      Stock: "#EF4444",
      Orders: "#8B5CF6",
      Sales: "#F59E0B",
      Returns: "#10B981",
      Management: "#06B6D4",
    };
    return colorMap[category] || "#6B7280";
  }

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <Icon sx={{ color: accountingColors.success, fontSize: 18 }}>
        check_circle
      </Icon>
    ) : (
      <Icon sx={{ color: accountingColors.error, fontSize: 18 }}>cancel</Icon>
    );
  };

  return (
    <Box>
      {/* Location Selector */}
      <Box sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Location</InputLabel>
          <Select
            value={selectedLocationId}
            onChange={(e) => onLocationChange(e.target.value as string)}
            label="Select Location"
            size="small"
          >
            <MenuItem value="">
              <em>Select location</em>
            </MenuItem>
            {locations.map((location) => (
              <MenuItem key={location._id} value={location._id}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={location.type}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: 20,
                      borderColor: accountingColors.secondary,
                      color: accountingColors.secondary,
                    }}
                  />
                  <span>{location.name}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Matrix Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => onTabChange(newValue)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
              color: accountingColors.secondary,
              "&.Mui-selected": {
                color: accountingColors.primary,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: accountingColors.primary,
            },
          }}
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <SecurityIcon fontSize="small" />
                <span>Permissions Matrix</span>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <AssignmentIcon fontSize="small" />
                <span>Roles Matrix</span>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <SettingsIcon fontSize="small" />
                <span>Role Management</span>
              </Stack>
            }
          />
        </Tabs>
      </Box>

      {/* Permissions Matrix Tab */}
      {activeTab === 0 && (
        <UserPermissions
          selectedLocationId={selectedLocationId}
          moduleKey={moduleKey}
          themeColor={themeColor}
          groupedPermissions={groupedPermissions}
          mockUsers={users}
          userPermissions={userPermissions}
          onPermissionToggle={onPermissionToggle}
          getPermissionIcon={getPermissionIcon}
          accountingColors={accountingColors}
          systemPermissions={systemPermissions}
          saving={saving}
          pendingChangesCount={pendingChangesCount}
          onSavePermissionChanges={onSavePermissionChanges}
        />
      )}

      {/* Roles Matrix Tab */}
      {activeTab === 1 && (
        <RolesMatrix
          selectedLocationId={selectedLocationId}
          moduleKey={moduleKey}
          themeColor={themeColor}
          mockUsers={users}
          userRoles={userRoles}
          onRoleToggle={onRoleToggle}
          accountingColors={accountingColors}
          saving={saving}
          pendingChangesCount={pendingChangesCount}
          onSaveRoleChanges={onSavePermissionChanges}
        />
      )}

      {/* Role Management Tab */}
      {activeTab === 2 && (
        <RolesDefinition
          moduleKey={moduleKey}
          themeColor={themeColor}
          accountingColors={accountingColors}
          systemPermissions={systemPermissions}
        />
      )}
    </Box>
  );
};
